'use client';

import { useTheme } from 'next-themes';
import Env, {
	VelocityTheme,
} from '../../environmentVariables/EnvironmentVariables';
import { MARKET_ICON_PATH } from 'src/constants/constants';
import { getBaseAssetSymbol } from '@velocity-exchange/common';
import { cm, cj } from 'src/utils/classNames';
import { CSSProperties, memo } from 'react';
import Image from 'next/image';
import { useIsolatedSpotSymbolToMainSpotSymbolMap } from 'src/hooks/spotMarkets/useIsolatedSpotSymbolToMainSpotMap';

const DEFAULT_SIZE = 16;

// Markets in here have base icons that have theme variants (e.g. apt-dark.svg, apt-light.svg)
const BASE_MARKET_SYMBOLS_WITH_THEME_VARIANTS_LIST: string[] = [];

// Markets in here use webp icons for all variants (i.e. base icon, grey dark/light icons)
const MARKET_SYMBOLS_USING_WEBP_LIST: string[] = [];

// Markets in here are a 1:1 mapping to their updated file name (e.g. sol_grey-dark: sol_grey-dark-v1.svg)
const ORIGINAL_FILE_NAME_TO_UPDATED_FILE_NAME_LOOKUP = {
	// The wrapped spot markets share their unwrapped counterpart's icon; the
	// asset bucket has no wbtc/weth file of its own.
	wbtc: 'btc.svg',
	weth: 'eth.svg',
};

type MarketIconFileNameParams = {
	baseSymbol: string;
	grey?: boolean;
	theme?: VelocityTheme;
};

const getMarketIconFileName = ({
	baseSymbol,
	grey,
	theme = VelocityTheme.dark,
}: MarketIconFileNameParams) => {
	let originalFileName = `${baseSymbol.toLowerCase()}${
		grey ? `_grey-${theme}` : ''
	}`;

	if (
		BASE_MARKET_SYMBOLS_WITH_THEME_VARIANTS_LIST.includes(baseSymbol) &&
		!grey
	) {
		originalFileName = `${baseSymbol}-${theme}`;
	}

	if (
		ORIGINAL_FILE_NAME_TO_UPDATED_FILE_NAME_LOOKUP[
			originalFileName as keyof typeof ORIGINAL_FILE_NAME_TO_UPDATED_FILE_NAME_LOOKUP
		]
	) {
		return ORIGINAL_FILE_NAME_TO_UPDATED_FILE_NAME_LOOKUP[
			originalFileName as keyof typeof ORIGINAL_FILE_NAME_TO_UPDATED_FILE_NAME_LOOKUP
		];
	}

	if (MARKET_SYMBOLS_USING_WEBP_LIST.includes(baseSymbol.toLowerCase())) {
		return `${originalFileName}.webp`;
	}

	return `${originalFileName}.svg`;
};

export interface MarketIconSrcParams extends MarketIconFileNameParams {
	/**
	 * Whether to use `/api/market-icon` as the src.
	 *
	 * This is necessary for loading icons within the PnL modal. If our Next API route
	 * isn't used, the icons will not be included in the image generated for download.
	 */
	useNextApiRoute?: boolean;
}

export const getMarketIconSrc = ({
	baseSymbol,
	grey,
	theme = VelocityTheme.dark,
	useNextApiRoute,
}: MarketIconSrcParams) => {
	const fileName = getMarketIconFileName({ baseSymbol, grey, theme });
	const src = useNextApiRoute
		? `/api/market-icon?path=${fileName}&cluster=${Env.sdkEnv}`
		: `${MARKET_ICON_PATH}/${fileName}`;

	return src;
};

interface TokenPairLogosProps extends Pick<
	MarketIconSrcParams,
	'useNextApiRoute'
> {
	fromTokenSymbol: string;
	toTokenSymbol: string;
	size?: number;
	leftImgFront?: boolean;
}

export const TokenPairLogos = ({
	fromTokenSymbol,
	toTokenSymbol,
	size,
	leftImgFront,
	useNextApiRoute,
}: TokenPairLogosProps) => {
	const theme = (useTheme().theme as VelocityTheme) ?? VelocityTheme.dark;

	if (!fromTokenSymbol || !toTokenSymbol) return null;

	const fromTokenLogo = getMarketIconSrc({
		baseSymbol: fromTokenSymbol,
		theme,
		grey: false,
		useNextApiRoute,
	});
	const toTokenLogo = getMarketIconSrc({
		baseSymbol: toTokenSymbol,
		theme,
		grey: false,
		useNextApiRoute,
	});

	return (
		<div className="-mr-sm flex shrink-0">
			<div>
				<img
					src={fromTokenLogo}
					alt=""
					className={cj('rounded-full', leftImgFront && 'relative z-10')}
					width={size ? size : DEFAULT_SIZE}
				/>
			</div>
			<div className="relative -left-[20%]">
				<img
					src={toTokenLogo}
					alt=""
					className="rounded-full"
					width={size ? size : DEFAULT_SIZE}
				/>
			</div>
		</div>
	);
};

interface MarketIconProps extends Pick<
	MarketIconSrcParams,
	'grey' | 'useNextApiRoute'
> {
	marketSymbol: string;
	className?: string;
	sizeClass?: string;
	customHeight?: number;
	customWidth?: number;
	isSpot?: boolean;
	style?: CSSProperties;
	pairSymbol?: string;
	useNextImageComponent?: boolean;
}

const MarketIcon = ({
	marketSymbol,
	className,
	grey,
	sizeClass: _sizeClass,
	customHeight,
	customWidth,
	isSpot,
	style,
	pairSymbol,
	useNextApiRoute,
	useNextImageComponent,
}: MarketIconProps) => {
	const isolatedSpotToMainSpotMarketSymbolMap =
		useIsolatedSpotSymbolToMainSpotSymbolMap();

	const hasCustomWidthAndHeight = customWidth && customHeight;

	const sizeClass =
		_sizeClass ??
		(hasCustomWidthAndHeight
			? `h-[${customHeight}px] w-[${customWidth}px]`
			: '');

	const overrideSymbol = isolatedSpotToMainSpotMarketSymbolMap[marketSymbol]; // Check if we should use an alternative/override marketSymbol in case the market is for an isolated spot market which may be for a "duplicate" asset

	const marketSymbolToUse = overrideSymbol ?? marketSymbol;

	// Spot market symbols are formatted as `BASE/QUOTE` (e.g. `SOL/USDT`).
	const [baseSymbolPart, quoteSymbolPart] = (marketSymbolToUse ?? '').split(
		'/'
	);
	const baseSymbol = getBaseAssetSymbol(baseSymbolPart, true).toLowerCase();
	const quoteSymbol = quoteSymbolPart?.toLowerCase() ?? 'usdt';

	const theme = (useTheme().theme as VelocityTheme) ?? VelocityTheme.dark;

	if (!marketSymbol) {
		return null;
	}

	// displays as pair logo between base token and the market's quote token
	if (isSpot) {
		return (
			<TokenPairLogos
				fromTokenSymbol={baseSymbol}
				toTokenSymbol={quoteSymbol}
				size={customWidth ?? 14}
				leftImgFront
				useNextApiRoute={useNextApiRoute}
			/>
		);
	}

	// displays as pair logo between base token and pairSymbol token
	if (pairSymbol) {
		return (
			<TokenPairLogos
				fromTokenSymbol={baseSymbol}
				toTokenSymbol={pairSymbol}
				size={customWidth ?? 14}
				leftImgFront
				useNextApiRoute={useNextApiRoute}
			/>
		);
	}

	const src = getMarketIconSrc({
		baseSymbol,
		theme,
		grey,
		useNextApiRoute,
	});

	// Apparently it needs both the classname and height/width to work for pngs and svgs
	if (useNextImageComponent) {
		return (
			<Image
				src={src}
				className={cm(sizeClass ?? 'h-[18px] w-[18px]', className)}
				width={customWidth ?? 18}
				height={customHeight ?? 18}
				alt={`${marketSymbol} icon`}
				style={style}
			/>
		);
	}
	return (
		<img
			src={src}
			className={cm(sizeClass ?? 'h-[18px] w-[18px]', className)}
			width={customWidth ?? 18}
			height={customHeight ?? 18}
			alt={`${marketSymbolToUse} icon`}
			style={style}
		/>
	);
};

export default memo(MarketIcon);
